import { PackageLimits } from '../../userPackage';
import { PackageType } from '../../../../types';

export type FeatureKey =
  | 'transcription'
  | 'askAI'
  | 'download'
  | 'priorityTranscription'
  | 'AINotes'
  | 'AISummaries'
  | 'integrations'
  | 'prioritySupport'
  | 'trainingSession'
  | 'customVocabulary'
  | 'fullTranscript'
  | 'AskAITimestamps'
  | 'advancedPlayback'
  | 'upload'
  | 'bulkUpload'
  | 'translateTranscript'
  | 'generalUpgrade'
  | 'skipQueue'
  | 'copyTranscript'
  | 'folderManagement'
  | 'login'
  | 'templates'
  | 'uploadButton'
  | 'importModalUploadButton'
  | 'unlimitedCredits'
  | 'customVocab'
  | 'importUrlButton'
  | 'youtubeInstagramImports'
  | 'multipleRecordingAskAI'
  | 'timeLimit'
  | 'screenRecorder'
  | 'audioRecorder'
  | 'downloadTranscript'
  | 'fullChapterAccess'
  | 'whiteLabel'
  | 'meetingBot'
  | 'VideoAnalyzer'
  | 'assistant'
  | 'dictationBestMode'
  | 'SceneDetection'
  | 'aiVideoDetection';

interface Feature {
  title: string;
  usageKey?: keyof PackageLimits;
  description: string;
  ranking: number;
  annualtitle?: string;
  annualDescription?: string;
  showContactSales?: boolean;
}

export type FeatureMap = Partial<{
  [key in FeatureKey]: Feature;
}>;

const freePlanFeatureMap: FeatureMap = {
  timeLimit: {
    title: 'features.timeLimit.title',
    description: 'features.timeLimit.description',
    ranking: 2,
  },
  transcription: {
    title: 'features.transcription.title',
    usageKey: 'transcriptionCount',
    description: 'features.transcription.description',
    ranking: 3,
  },
  AISummaries: {
    title: 'features.aiSummaries.title',
    description: 'features.aiSummaries.description',
    ranking: 4,
  },
  AINotes: {
    title: 'features.aiNotes.title',
    description: 'features.aiNotes.description',
    ranking: 5,
  },
  screenRecorder: {
    title: 'features.screenRecorder.title',
    description: 'features.screenRecorder.description',
    ranking: 6,
  },
  audioRecorder: {
    title: 'features.audioRecorder.title',
    description: 'features.audioRecorder.description',
    ranking: 7,
  },
  askAI: {
    title: 'features.askAI.title',
    usageKey: 'askAICount',
    description: 'features.askAI.description',
    ranking: 8,
  },
};

export const growthFeatureMap: FeatureMap = {
  transcription: {
    title: 'features.transcription.titleSmart',
    usageKey: 'transcriptionCount',
    description: 'features.transcription.descriptionSmart',
    ranking: 1,
    annualtitle: 'features.transcription.annualTitle',
    annualDescription: 'features.transcription.annualDescription',
  },
  askAI: {
    title: 'features.askAI.titleChat',
    usageKey: 'askAICount',
    description: 'features.askAI.descriptionChat',
    ranking: 2,
    annualtitle: 'features.askAI.annualTitle',
    annualDescription: 'features.askAI.annualDescription',
  },
  // librarySize feature removed - library size limit hidden from UI
  fullTranscript: {
    title: 'features.fullTranscript.title',
    description: 'features.fullTranscript.description',
    ranking: 4,
  },
  templates: {
    title: 'features.templates.title',
    description: 'features.templates.description',
    ranking: 5,
  },
  fullChapterAccess: {
    title: 'features.fullChapterAccess.title',
    description: 'features.fullChapterAccess.description',
    ranking: 6,
  },
  multipleRecordingAskAI: {
    title: 'features.multipleRecordingAskAI.title',
    description: 'features.multipleRecordingAskAI.description',
    ranking: 7,
  },
  assistant: {
    title: 'features.assistant.title',
    description: 'features.assistant.description',
    ranking: 9,
  },
  prioritySupport: {
    title: 'features.prioritySupport.title',
    description: 'features.prioritySupport.description',
    ranking: 10,
  },
  trainingSession: {
    title: 'features.trainingSession.title',
    description: 'features.trainingSession.description',
    ranking: 11,
  },
  download: {
    title: 'features.download.title',
    usageKey: 'mp4DownloadCount',
    description: 'features.download.description',
    ranking: 12,
  },
  advancedPlayback: {
    title: 'features.advancedPlayback.title',
    description: 'features.advancedPlayback.description',
    ranking: 13,
  },
  translateTranscript: {
    title: 'features.translateTranscript.title',
    description: 'features.translateTranscript.description',
    ranking: 14,
  },
  generalUpgrade: {
    title: 'features.generalUpgrade.title',
    usageKey: 'generalUpgrade',
    description: 'features.generalUpgrade.description',
    ranking: 15,
  },
  priorityTranscription: {
    title: 'features.priorityTranscription.title',
    usageKey: 'priority',
    description: 'features.priorityTranscription.description',
    ranking: 16,
  },
  copyTranscript: {
    title: 'features.copyTranscript.title',
    usageKey: 'copyTranscriptCount',
    description: 'features.copyTranscript.description',
    ranking: 17,
  },
  upload: {
    title: 'features.upload.title',
    usageKey: 'uploadCount',
    description: 'features.upload.description',
    ranking: 18,
  },
  folderManagement: {
    title: 'features.folderManagement.title',
    description: 'features.folderManagement.description',
    ranking: 8,
  },
  login: {
    title: 'features.login.title',
    description: 'features.login.description',
    ranking: 20,
  },
  AskAITimestamps: {
    title: 'features.askAITimestamps.title',
    description: 'features.askAITimestamps.description',
    ranking: 21,
  },
  uploadButton: {
    title: 'features.uploadButton.title',
    description: 'features.uploadButton.description',
    ranking: 22,
  },
  importUrlButton: {
    title: 'features.importUrlButton.title',
    description: 'features.importUrlButton.description',
    ranking: 23,
  },
  youtubeInstagramImports: {
    title: 'features.youtubeInstagramImports.title',
    description: 'features.youtubeInstagramImports.description',
    ranking: 24,
  },
  downloadTranscript: {
    title: 'features.downloadTranscript.title',
    usageKey: 'copyTranscriptCount',
    description: 'features.downloadTranscript.description',
    ranking: 25,
  },
  importModalUploadButton: {
    title: 'features.importModalUploadButton.title',
    description: 'features.importModalUploadButton.description',
    ranking: 26,
  },
  meetingBot: {
    title: 'features.meetingBot.title',
    description: 'features.meetingBot.description',
    ranking: 26,
  },
  VideoAnalyzer: {
    title: 'features.videoAnalyzer.title',
    usageKey: 'videoAskAICount',
    description: 'features.videoAnalyzer.description',
    ranking: 33,
    showContactSales: true,
  },
  dictationBestMode: {
    title: 'features.dictationBestMode.title',
    description: 'features.dictationBestMode.description',
    ranking: 34,
  },
  SceneDetection: {
    title: 'features.sceneDetection.title',
    description: 'features.sceneDetection.description',
    ranking: 35,
  },
  aiVideoDetection: {
    title: 'features.aiVideoDetection.title',
    description: 'features.aiVideoDetection.description',
    ranking: 36,
  },
};

const businessFeatureMap: FeatureMap = {
  transcription: {
    title: 'features.transcription.titleEverything',
    usageKey: 'transcriptionCount',
    description: 'features.transcription.descriptionEverything',
    ranking: 1,
  },
  askAI: {
    title: 'features.askAI.titleAnything',
    usageKey: 'askAICount',
    description: 'features.askAI.descriptionAnything',
    ranking: 2,
  },
  // librarySize feature removed - library size limit hidden from UI
  fullTranscript: {
    title: 'features.fullTranscript.title',
    description: 'features.fullTranscript.description',
    ranking: 4,
  },
  templates: {
    title: 'features.templates.title',
    description: 'features.templates.description',
    ranking: 5,
  },
  fullChapterAccess: {
    title: 'features.fullChapterAccess.title',
    description: 'features.fullChapterAccess.description',
    ranking: 6,
  },
  multipleRecordingAskAI: {
    title: 'features.multipleRecordingAskAI.title',
    description: 'features.multipleRecordingAskAI.description',
    ranking: 7,
  },
  assistant: {
    title: 'features.assistant.title',
    description: 'features.assistant.description',
    ranking: 9,
  },
  prioritySupport: {
    title: 'features.prioritySupport.title',
    description: 'features.prioritySupport.descriptionDedicated',
    ranking: 10,
  },
  trainingSession: {
    title: 'features.trainingSession.title',
    description: 'features.trainingSession.descriptionPersonalized',
    ranking: 11,
  },
  download: {
    title: 'features.download.title',
    usageKey: 'mp4DownloadCount',
    description: 'features.download.description',
    ranking: 12,
  },
  advancedPlayback: {
    title: 'features.advancedPlayback.title',
    description: 'features.advancedPlayback.description',
    ranking: 13,
  },
  integrations: {
    title: 'features.integrations.title',
    description: 'features.integrations.description',
    ranking: 14,
    showContactSales: true,
  },
  customVocabulary: {
    title: 'features.customVocabulary.title',
    description: 'features.customVocabulary.description',
    ranking: 15,
  },
  bulkUpload: {
    title: 'features.bulkUpload.title',
    description: 'features.bulkUpload.description',
    ranking: 16,
  },
  translateTranscript: {
    title: 'features.translateTranscript.title',
    description: 'features.translateTranscript.description',
    ranking: 17,
  },
  generalUpgrade: {
    title: 'features.generalUpgrade.title',
    description: 'features.generalUpgrade.description',
    ranking: 18,
  },
  priorityTranscription: {
    title: 'features.priorityTranscription.title',
    description: 'features.priorityTranscription.description',
    ranking: 19,
  },
  copyTranscript: {
    title: 'features.copyTranscript.title',
    usageKey: 'copyTranscriptCount',
    description: 'features.copyTranscript.description',
    ranking: 20,
  },
  upload: {
    title: 'features.upload.title',
    usageKey: 'uploadCount',
    description: 'features.upload.description',
    ranking: 21,
  },
  folderManagement: {
    title: 'features.folderManagement.title',
    description: 'features.folderManagement.descriptionOrganize',
    ranking: 8,
  },
  login: {
    title: 'features.login.title',
    description: 'features.login.description',
    ranking: 23,
  },
  AskAITimestamps: {
    title: 'features.askAITimestamps.title',
    description: 'features.askAITimestamps.description',
    ranking: 24,
  },
  customVocab: {
    title: 'features.customVocab.title',
    description: 'features.customVocab.description',
    ranking: 25,
  },
  uploadButton: {
    title: 'features.uploadButton.title',
    description: 'features.uploadButton.description',
    ranking: 26,
  },
  unlimitedCredits: {
    title: 'features.unlimitedCredits.title',
    description: 'features.unlimitedCredits.description',
    ranking: 27,
  },
  importUrlButton: {
    title: 'features.importUrlButton.title',
    description: 'features.importUrlButton.description',
    ranking: 28,
  },
  youtubeInstagramImports: {
    title: 'features.youtubeInstagramImports.title',
    description: 'features.youtubeInstagramImports.description',
    ranking: 29,
  },
  downloadTranscript: {
    title: 'features.downloadTranscript.title',
    usageKey: 'copyTranscriptCount',
    description: 'features.downloadTranscript.description',
    ranking: 30,
  },
  importModalUploadButton: {
    title: 'features.importModalUploadButton.title',
    description: 'features.importModalUploadButton.description',
    ranking: 31,
  },
  meetingBot: {
    title: 'features.meetingBot.title',
    description: 'features.meetingBot.description',
    ranking: 32,
    showContactSales: true,
  },
  whiteLabel: {
    title: 'features.whiteLabel.title',
    description: 'features.whiteLabel.description',
    ranking: 33,
    showContactSales: true,
  },
  VideoAnalyzer: {
    title: 'features.videoAnalyzer.title',
    usageKey: 'videoAskAICount',
    description: 'features.videoAnalyzer.description',
    ranking: 34,
    showContactSales: true,
  },
  dictationBestMode: {
    title: 'features.dictationBestMode.title',
    description: 'features.dictationBestMode.description',
    ranking: 35,
  },
  SceneDetection: {
    title: 'features.sceneDetection.title',
    description: 'features.sceneDetection.description',
    ranking: 36,
  },
  aiVideoDetection: {
    title: 'features.aiVideoDetection.title',
    description: 'features.aiVideoDetection.description',
    ranking: 37,
  },
};

const enterpriseFeatureMap: FeatureMap = {
  ...businessFeatureMap,
};

const standardFeaturesMap = growthFeatureMap;

const premiumFeaturesMap = growthFeatureMap;

const planFeatures: Partial<{
  [key in PackageType]: FeatureMap;
}> = {
  FREE_LOGGEDIN: freePlanFeatureMap,
  STANDARD: standardFeaturesMap,
  PREMIUM: premiumFeaturesMap,
  ENTERPRISE: enterpriseFeatureMap,
  BUSINESS: businessFeatureMap,
  GROWTH: growthFeatureMap,
};

export default planFeatures;
